/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Michael Kokoulin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.0
 */
import { Children, cloneElement, Fragment, memo, ReactNode, useEffect } from 'react';

import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector, ParametricSelector } from 'reselect';

import { IRootReducer } from '../../../reducers/rootReducer';
import { ValueOf } from '../../../types/globalTypes';
import { initTranslation, TranslationFileNamesEnum } from '../constants';
import { loadInternalTranslationsStart, setLocale } from '../intActions';
import { ILocaleEnum, LocaleEnum } from '../intConstants';
import { makeSelectLocale, makeSelectTranslation } from '../intSelectors';
import { ITranslation } from '../types';

export interface IPageLocaleOwnProps {
    fileNames: TranslationFileNamesEnum[];
    children: ReactNode | Element;
    getTranslation: () => void;
    lazy?: boolean;
    setLocale: () => void;
}

export interface IPageLocaleConnectedProps {
    translation: ITranslation;
    locale: ValueOf<ILocaleEnum>;
}

type TPageLocale = IPageLocaleOwnProps & IPageLocaleConnectedProps

const PageLocale = (props: TPageLocale) => {

    useEffect(() => {
        props.getTranslation();
    }, [props.locale]);

    if (props.lazy) {
        return null;
    }

    let translation = {};
    props.fileNames.forEach((name) => {
        translation = {
            ...translation,
            ...props.translation[name],
        };
    });
    const isLocaleSet = Object.entries(translation).length > 0;
    const childrenWithProps = Children.map(props.children, (child: any) => {
        return child && cloneElement(child, { translation });
    });
    return (
        <Fragment>
            {isLocaleSet ? childrenWithProps : <Fragment/>}
        </Fragment>
    );
};

PageLocale.defaultProps = {
    fileNames: [TranslationFileNamesEnum.common],
    children: <div>No component</div>,
    getTranslation: () => null,
    setLocale: () => null,
    locale: LocaleEnum.ru,
    translation: initTranslation,
};

const mapStateToProps: ParametricSelector<IRootReducer, IPageLocaleOwnProps, IPageLocaleConnectedProps> =
    createStructuredSelector<IRootReducer, IPageLocaleConnectedProps>({
        translation: makeSelectTranslation(),
        locale: makeSelectLocale(),
    });

const mapDispatchToProps = (dispatch: Dispatch, props: TPageLocale) => ({
    getTranslation: () => dispatch(loadInternalTranslationsStart(props.fileNames)),
    setLocale: () => dispatch(setLocale(LocaleEnum.ru)),
});

const withConnect = connect(
    mapStateToProps,
    mapDispatchToProps,
);

export default compose(
    withConnect,
    memo,
)(PageLocale as any) as any;
